import * as React from 'react';
import { lazy, Suspense, useCallback, useState } from 'react';
import { Route, RouteComponentProps, Switch, withRouter } from 'react-router-dom';
import { Redirect, useRoutePath, AlternativeRoutingRedirector } from '@shared/routing';
import { useTranslation } from 'react-i18next';
import Helmet from 'react-helmet';

import PromoLoader from '../../PromoLoader/components/PromoLoader';
import { StartSearch } from '../../Results/types';
import Checkout from '../../Checkout';
import { DelayedPromoLoader } from '../../PromoLoader';
import { useConfig } from '../../context';
import Page404 from '../../Page404/Page404';
import { API_DATE_FORMAT, format, isRequireCurrencyParam } from '../../utils';
import { useTheme } from '../../theme';
import { useRenderers } from '../../renderProps';
import {
	AviaSearchResult,
	CheckoutOrderFragment,
	useRunSearchMutation,
	SearchResultsQuery,
	useSearchResultsQuery
} from '@websky/graphql';
import { useSchedule } from '../../Schedule/hooks';
import { buildDefaultParameters } from '../../Schedule/lib/buildDefaultParameters';
import { CURRENCY_KEY, set } from '../../cache';
import getOrderDataLayer, { createDataLayer } from '../../DataLayerAnalytics/orderDataLayerInstance';
import { OrderType } from '../../DataLayerAnalytics/OrderDataLayer/types';
import { initTagManager } from '../../DataLayerAnalytics/tagManager';
import { extractPromo, hasPromo } from './FastSearch/utils';
import { ApolloError } from 'apollo-boost';
import { EmptyPricesForFareGroupsError, EmptySearchResultSegmentsError } from './errors';
import { useCurrentUser } from '../../CurrentUser/CurrentUser.hooks';
import ConfirmOptions from './ConfirmOptions/ConfirmOptions';
import { getUrlWithServicesParams } from '../../Checkout/utils';
import { ScheduleSearchData } from '../../Schedule/types';
import { FiltersState } from '../../Schedule/store/filters/reducers';
import { Log } from '../../sentry';

const SearchForm = lazy(() => import('../../SearchForm'));
const RestoreOrder = lazy(() => import('./RestoreOrder/RestoreOrder'));
const FindOrder = lazy(() => import('../../Checkout/components/Checkout/FindOrder/FindOrder'));
const MetaRedirect = lazy(() => import('./MetaRedirect/MetaRedirect'));
const ResultsError = lazy(() => import('../../ResultsError'));
const FastSearch = lazy(() => import('./FastSearch/FastSearch'));
const CreateOrder = lazy(() => import('./CreateOrder/CreateOrder'));
const ResultsWrapper = lazy(() => import('../../Results/components/ResultsWrapper/ResultsWrapper'));
const Schedule = lazy(() => import('../../Schedule'));

createDataLayer(OrderType.SearchResults);

const Inner: React.FunctionComponent<RouteComponentProps> = ({ history, location }) => {
	useCurrentUser();
	const { t } = useTranslation();
	const { Inner: css } = useTheme('Inner');
	const [isRedirectFromMeta, setIsRedirectFromMeta] = useState(false);
	const [order, setOrder] = useState<CheckoutOrderFragment>(null);
	const [results, setResults] = useState<SearchResultsQuery['SearchResult']>();
	const [error, setError] = useState<Error>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isConfirmOptionLoading, setIsConfirmOptionLoading] = useState(false);
	const [orderLoading, setOrderLoading] = useState(false);
	const {
		Engine: { searchFormURL },
		global: {
			companyInfo: { iataCode },
			allowPromoCodes
		}
	} = useConfig();
	const [searchMutation] = useRunSearchMutation();
	const resultsQuery = useSearchResultsQuery({ fetchPolicy: 'no-cache', skip: true });
	const [promoCode, setPromoCode] = useState<string>(undefined);
	const routePath = useRoutePath();

	const { SearchForm: SearchFormOverride = SearchForm, FastSearch: FastSearchOverride = FastSearch } = useRenderers();

	React.useEffect(() => {
		initTagManager();
	}, []);

	const handleOrderLoaded = useCallback(
		(order: CheckoutOrderFragment) => {
			setOrder(order as CheckoutOrderFragment);
		},
		[order, setOrder]
	);

	const orderLoadingHandler = useCallback(orderLoading => {
		setOrderLoading(orderLoading);
	}, []);

	const handleRenderConfirmOptions = () => {
		if (order && !orderLoading) {
			getOrderDataLayer(OrderType.Checkout).setOrder(order as CheckoutOrderFragment);

			return (
				<ConfirmOptions
					order={order}
					handleOrderLoaded={handleOrderLoaded}
					onLoadingChange={setIsConfirmOptionLoading}
				/>
			);
		}

		return null;
	};

	const [getSchedule, { schedule, defaultDate, availableDates, isLoading: scheduleLoading }] = useSchedule();

	const dismissMetaWarning = useCallback(() => {
		setIsRedirectFromMeta(false);
	}, []);

	const onError = useCallback(
		(e: unknown) => {
			let error: Error = new Error();
			error.name = t('It looks like something went wrong');
			error.message = t('Please reload the page or start a new search');

			if (e instanceof ApolloError && e.graphQLErrors?.[0]?.originalError) {
				error = e.graphQLErrors[0].originalError;
			} else if (e instanceof Error) {
				error = e;
			}

			setError(error);
			console.error(e);
		},
		[setError]
	);

	const loadResults = useCallback(
		async (id: string) => {
			setOrder(null);
			setIsLoading(true);

			try {
				const { data } = await resultsQuery.refetch({ id });

				if (!data?.SearchResult?.searchParameters?.segments?.length) {
					throw new EmptySearchResultSegmentsError();
				}

				if (
					data?.SearchResult?.flightDirections?.some(direction =>
						direction.legs.every(leg => !leg.pricesForFareGroups)
					)
				) {
					Log({
						message: EmptyPricesForFareGroupsError.name,
						data: data.SearchResult,
						level: 'error'
					});

					throw new EmptyPricesForFareGroupsError();
				}

				setResults(data?.SearchResult ?? null);
			} catch (error) {
				onError(error);
			}

			setIsLoading(false);
		},
		[resultsQuery.refetch, setOrder, setIsLoading, onError]
	);

	const startSearch: StartSearch = useCallback(async (params, replace = true) => {
		setIsLoading(true);
		setError(null);

		if (params.currency) {
			set(CURRENCY_KEY, params.currency);
		}

		if (replace) {
			history.replace('/results');
		} else {
			history.push('/results');
		}

		try {
			const data = await searchMutation({
				variables: {
					params: {
						...params,
						currency: isRequireCurrencyParam() ? params.currency : undefined
					}
				}
			});

			if (data.errors) {
				throw new Error(data.errors[0].message);
			} else if (data.data.RunGeneralSearch && data.data.RunGeneralSearch.id) {
				if (replace) {
					history.push('/results/' + data.data.RunGeneralSearch.id);
				} else {
					history.replace('/results/' + data.data.RunGeneralSearch.id);
				}

				loadResults(data.data.RunGeneralSearch.id);
			} else {
				throw new Error('No results ID');
			}
		} catch (error) {
			setError({
				name: t('It looks like something went wrong'),
				message: `${t('Please reload the page or start a new search')}`
			});

			console.error(error);

			setIsLoading(false);
		}
	}, []);

	const onOrderRecieved = useCallback(
		(order: CheckoutOrderFragment, replace = false) => {
			if (order) {
				setOrder(order as CheckoutOrderFragment);
			}

			history.push(getUrlWithServicesParams(`/booking/${order.id}`, location));
		},
		[history, location]
	);

	const redirectToPayment = (order: CheckoutOrderFragment) => {
		if (order) {
			setOrder(order as CheckoutOrderFragment);
			history.push(`/booking/${order.id}/payment`);
		}
	};

	const onMetasearchResultsRedirect = (resultsId: string) => {
		setIsRedirectFromMeta(true);
		history.replace('/results/' + resultsId);
	};

	const onScheduleSearch = useCallback(
		({ parameters, filters, date }: ScheduleSearchData) => {
			const pathname = history.location.pathname;
			const directOnlyParam = filters?.directOnly ? '/directOnly' : '';

			if (date) {
				getSchedule(parameters, date);
				const newPath = `${pathname.replace(/\/directOnly\/?$/, '')}${directOnlyParam}`;

				if (pathname !== newPath) {
					history.push(newPath);
				}
			} else {
				const path = `/schedule/${parameters.departure}/${parameters.arrival}${directOnlyParam}`;

				if (pathname !== path) {
					history.push(path);
				}
			}
		},
		[history, getSchedule]
	);

	const isScheduleAvailable = ['D2', 'ZZ', 'YC', 'DV', 'KV', 'Y7', '6R', 'R3'].includes(iataCode);

	return (
		<div style={{ flexBasis: '100%' }} className={css.inner}>
			<Helmet>
				<title>
					{t('Title:companyName')} &ndash; {t('Title:Find tickets')}
				</title>
			</Helmet>

			<Suspense fallback={<PromoLoader />}>
				<AlternativeRoutingRedirector />
				<DelayedPromoLoader loading={isLoading || scheduleLoading}>
					<Switch>
						<Route
							path={routePath('/')}
							exact
							render={() => {
								if (searchFormURL) {
									window.location.href = searchFormURL;

									return null;
								}

								return <SearchFormOverride onSubmit={startSearch} promoCode={promoCode} />;
							}}
						/>

						<Route
							path={routePath('/search/:departure/:arrival')}
							exact
							render={routeProps => (
								<SearchFormOverride
									key={routeProps.location.pathname}
									onSubmit={startSearch}
									defaultParameters={{
										currency: null,
										segments: [
											{
												arrival: {
													iata: routeProps.match.params['arrival'],
													city: null,
													name: null
												},
												departure: {
													iata: routeProps.match.params['departure'],
													city: null,
													name: null
												},
												date: format(new Date(), API_DATE_FORMAT)
											}
										],
										passengers: null,
										promotionCode: null,
										ffpMode: null
									}}
								/>
							)}
						/>

						<Route
							path={routePath('/restore/:orderId/:token')}
							exact
							render={() => (
								<RestoreOrder
									onOrderReceived={
										onOrderRecieved as (order: CheckoutOrderFragment, reload?: boolean) => void
									}
								/>
							)}
						/>

						<Route
							path={routePath(['/find/:id?/:secret?', '/find/:id?/:secret?/submit'])}
							exact
							render={routeProps => {
								const callSubmit = routeProps.match.path.includes('submit');

								return <FindOrder onSuccess={onOrderRecieved} callSubmit={callSubmit} />;
							}}
						/>

						<Route
							path={routePath(['/payment/:id?/:secret?', '/payment/:id?/:secret?/submit'])}
							exact
							render={routeProps => {
								const callSubmit = routeProps.match.path.includes('submit');

								return <FindOrder onSuccess={redirectToPayment} callSubmit={callSubmit} />;
							}}
						/>

						<Route
							path={routePath('/metasearch/:token')}
							exact
							render={() => (
								<MetaRedirect
									onResultsRedirect={onMetasearchResultsRedirect}
									onSuccess={onOrderRecieved}
								/>
							)}
						/>

						<Route
							path={routePath('/results')}
							exact
							render={() => {
								if (error) {
									return (
										<>
											<SearchFormOverride onSubmit={startSearch} />
											<ResultsError header={error.name} body={error.message} />
										</>
									);
								}

								return !error && !isLoading ? <Redirect to="/" /> : null;
							}}
						/>

						<Route
							path={routePath('/search/:fastSearch')}
							exact
							render={props => (
								<FastSearchOverride
									startSearch={startSearch}
									fastSearch={props.match.params.fastSearch}
									redirect={<Redirect to="/" />}
								/>
							)}
						/>

						<Route
							path={routePath('/results/create-order/:flightIds+')}
							render={props => (
								<CreateOrder
									flightsIds={props.match.params.flightIds}
									redirectOnFail={<Redirect to={'/'} />}
									onSuccess={onOrderRecieved}
								/>
							)}
						/>

						<Route
							path={routePath('/results/:id(\\d+/?)+')}
							render={props => {
								return (
									<ResultsWrapper
										order={order}
										isOrderLoading={orderLoading}
										isConfirmOptionLoading={isConfirmOptionLoading}
										renderConfirmOptions={handleRenderConfirmOptions}
										showMetaWarning={isRedirectFromMeta}
										onMetaWarningDismiss={dismissMetaWarning}
										onOrderLoaded={handleOrderLoaded}
										onOrderLoading={orderLoadingHandler}
										searchResults={results as AviaSearchResult}
										errors={error ? [error] : null}
										startNewSearch={startSearch}
										onProceed={onOrderRecieved}
										resultId={props.match.params.id}
										loadResults={loadResults}
										isLoading={isLoading}
										error={error}
									/>
								);
							}}
						/>

						<Route
							path={routePath('/booking/:id(\\d+/?)+')}
							render={props => (
								<>
									<Helmet>
										<title>
											{t('Title:companyName')} &ndash; {t('Title:Checkout order')}
										</title>
									</Helmet>

									<Checkout
										startNewSearch={startSearch}
										orderId={props.match.params.id as string}
										order={order}
									/>
								</>
							)}
						/>

						{isScheduleAvailable && (
							<Route
								path={routePath('/schedule')}
								exact
								render={() => {
									if (scheduleLoading) {
										return null;
									}

									return <Schedule onScheduleSearch={onScheduleSearch} />;
								}}
							/>
						)}

						{isScheduleAvailable && (
							<Route
								path={routePath(
									'/schedule/:departure([A-ZА-ЯЁ]{3})/:arrival([A-ZА-ЯЁ]{3})/:filters(directOnly)?'
								)}
								render={props => {
									const params = props.match.params;

									const segment = schedule?.searchParameters?.segments?.[0];

									const filters: FiltersState = {
										directOnly: !!params.filters
									};

									const defaultParameters = buildDefaultParameters({
										departure: params.departure,
										arrival: params.arrival,
										date: defaultDate,
										searchParameters: schedule?.searchParameters
									});

									const isOtherCities =
										segment &&
										(segment.departure?.iata !== params.departure ||
											segment.arrival?.iata !== params.arrival);

									if (
										(!isLoading && !scheduleLoading && !schedule) ||
										(isOtherCities && !scheduleLoading)
									) {
										getSchedule({ departure: params.departure, arrival: params.arrival });

										return null;
									}

									if (scheduleLoading) {
										return null;
									}

									return (
										<Schedule
											defaultParameters={defaultParameters}
											filters={filters}
											schedule={schedule}
											availableDates={availableDates}
											onStartSearch={startSearch}
											onScheduleSearch={onScheduleSearch}
										/>
									);
								}}
							/>
						)}

						<Route
							path={routePath('/:fastSearch')}
							exact
							render={props => {
								const searchParams = props.match.params.fastSearch;
								if (allowPromoCodes && hasPromo(searchParams)) {
									const promoCode = extractPromo(searchParams);

									if (promoCode) {
										setPromoCode(extractPromo(searchParams));
									}
								}

								return <Redirect to="/" />;
							}}
						/>

						<Page404 onClick={() => (window.location.href = '/')} />
					</Switch>
				</DelayedPromoLoader>
			</Suspense>
		</div>
	);
};

export default withRouter(Inner);
